import { useState, useMemo } from 'react';
import { useReport, csvHref } from '../../hooks/useReports.js';
import { useAuth } from '../../hooks/useAuth.js';
import { weekRangeOffset, centsToUsd, iso } from '../../lib/format.js';

const KINDS = [
  { id: 'hours', label: 'Hours',     roles: ['admin', 'foreman', 'bookkeeper'] },
  { id: 'pay',   label: 'Pay',       roles: ['admin', 'bookkeeper'] },
  { id: 'jha',   label: 'JHA',       roles: ['admin', 'foreman', 'bookkeeper'] },
  { id: 'docs',  label: 'Documents', roles: ['admin', 'foreman', 'bookkeeper'] },
];

// Reports: pick a date range and one of four lenses. CSV download per tab.
export default function ReportsPage() {
  const { role } = useAuth();
  const [preset, setPreset] = useState('this-week');
  const [kind, setKind] = useState('hours');
  const range = useMemo(() => resolveRange(preset), [preset]);
  const visibleKinds = KINDS.filter(k => k.roles.includes(role));
  const activeKind = visibleKinds.find(k => k.id === kind) ? kind : visibleKinds[0]?.id;

  return (
    <div>
      <div className="range-bar" style={{ gap: 8, flexWrap: 'wrap' }}>
        <select className="field-select" value={preset} onChange={e => setPreset(e.target.value)}>
          <option value="this-week">This week</option>
          <option value="last-week">Last week</option>
          <option value="this-month">This month</option>
          <option value="last-month">Last month</option>
          <option value="ytd">Year to date</option>
        </select>
        <span style={{ color: 'var(--warm-gray)', fontSize: 11 }}>
          {iso(range.from)} → {iso(range.to - 1)}
        </span>
      </div>

      <div className="tab-bar" style={{ marginTop: 12 }}>
        {visibleKinds.map(k => (
          <button
            key={k.id}
            className={'tab' + (k.id === activeKind ? ' active' : '')}
            onClick={() => setKind(k.id)}
          >
            <span className="tab-label">{k.label}</span>
          </button>
        ))}
        <a
          className="tab"
          style={{ marginLeft: 'auto' }}
          href={csvHref({ kind: activeKind, from: range.from, to: range.to })}
        >
          <i className="ph ph-download-simple" /> CSV
        </a>
      </div>

      <div style={{ marginTop: 12 }}>
        {activeKind === 'hours' && <HoursReport range={range} />}
        {activeKind === 'pay' && <PayReport range={range} />}
        {activeKind === 'jha' && <JhaReport range={range} />}
        {activeKind === 'docs' && <DocsReport />}
      </div>
    </div>
  );
}

function resolveRange(preset) {
  if (preset === 'this-week') return weekRangeOffset(0);
  if (preset === 'last-week') return weekRangeOffset(-1);
  const now = new Date();
  if (preset === 'this-month') {
    const f = +new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const t = +new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
    return { from: f, to: t };
  }
  if (preset === 'last-month') {
    const f = +new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
    const t = +new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    return { from: f, to: t };
  }
  if (preset === 'ytd') {
    const f = +new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
    return { from: f, to: Date.now() };
  }
  return weekRangeOffset(0);
}

function HoursReport({ range }) {
  const { data, isLoading } = useReport({ kind: 'hours', from: range.from, to: range.to });
  if (isLoading || !data) return <div className="loading">Loading…</div>;
  return (
    <table className="tbl">
      <thead><tr><th>Crew</th><th className="num">Reg</th><th className="num">OT</th><th className="num">Total</th></tr></thead>
      <tbody>
        {data.rows.map(r => (
          <tr key={r.user_id}>
            <td>{r.user_name}</td>
            <td className="num">{r.regular_hrs.toFixed(2)}</td>
            <td className="num" style={{ color: r.ot_hrs > 0 ? 'var(--orange)' : 'inherit' }}>{r.ot_hrs.toFixed(2)}</td>
            <td className="num"><strong>{r.total_hrs.toFixed(2)}</strong></td>
          </tr>
        ))}
        <tr style={{ fontWeight: 700 }}>
          <td>TOTAL</td>
          <td className="num">{data.totals.regular_hrs.toFixed(2)}</td>
          <td className="num">{data.totals.ot_hrs.toFixed(2)}</td>
          <td className="num">{data.totals.total_hrs.toFixed(2)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function PayReport({ range }) {
  const { data, isLoading } = useReport({ kind: 'pay', from: range.from, to: range.to });
  if (isLoading || !data) return <div className="loading">Loading…</div>;
  return (
    <table className="tbl">
      <thead><tr><th>Crew</th><th className="col-meta">Type</th><th className="num col-meta">Reg pay</th><th className="num col-meta">OT pay</th><th className="num">Total</th></tr></thead>
      <tbody>
        {data.rows.map(r => (
          <tr key={r.user_id}>
            <td>{r.user_name}</td>
            <td className="col-meta"><span className="badge">{r.employment_type}</span></td>
            <td className="num col-meta">{centsToUsd(r.regular_pay_cents)}</td>
            <td className="num col-meta">{centsToUsd(r.ot_pay_cents)}</td>
            <td className="num"><strong>{centsToUsd(r.total_pay_cents)}</strong></td>
          </tr>
        ))}
        <tr style={{ fontWeight: 700 }}>
          <td>TOTAL</td><td className="col-meta"></td>
          <td className="num col-meta">{centsToUsd(data.totals.regular_pay_cents)}</td>
          <td className="num col-meta">{centsToUsd(data.totals.ot_pay_cents)}</td>
          <td className="num">{centsToUsd(data.totals.total_pay_cents)}</td>
        </tr>
      </tbody>
    </table>
  );
}

function JhaReport({ range }) {
  const { data, isLoading } = useReport({ kind: 'jha', from: range.from, to: range.to });
  if (isLoading || !data) return <div className="loading">Loading…</div>;
  const dayLabels = data.days.map(d => new Date(d).toISOString().slice(5, 10));
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="tbl">
        <thead>
          <tr>
            <th>Crew</th>
            {dayLabels.map(d => <th key={d} style={{ fontSize: 10, textAlign: 'center' }}>{d}</th>)}
            <th className="num">%</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.user_id}>
              <td>{r.user_name}</td>
              {r.cells.map((c, i) => (
                <td key={i} style={{
                  textAlign: 'center',
                  color: c ? 'var(--orange)' : 'var(--text-muted)',
                  fontWeight: c ? 700 : 400,
                }}>{c ? '✓' : '·'}</td>
              ))}
              <td className="num">{r.compliance_pct}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocsReport() {
  const range = { from: Date.now() - 365 * 86400000, to: Date.now() + 365 * 86400000 };
  const { data, isLoading } = useReport({ kind: 'docs', from: range.from, to: range.to });
  if (isLoading || !data) return <div className="loading">Loading…</div>;
  return (
    <table className="tbl">
      <thead><tr><th>Crew</th><th>Kind</th><th>Label</th><th>Expires</th><th>Verified</th></tr></thead>
      <tbody>
        {data.rows.map(d => (
          <tr key={d.id}>
            <td>{d.user_name}</td>
            <td><span className="badge">{d.kind}</span></td>
            <td>{d.label || '—'}</td>
            <td>{d.expires_at ? new Date(d.expires_at).toLocaleDateString() : '—'}</td>
            <td>{d.verified_at ? <span className="badge orange">✓</span> : <span style={{ color: 'var(--text-muted)' }}>no</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
